import { useEffect, useRef, useState, useCallback } from 'react'
import draftService from '../services/draftService'
import { useAuth } from '../contexts/AuthContext'
import { getImageReferenceName } from '../utils/imageMentions'

/**
 * Custom Hook für Auto-Save von Entwürfen
 * @param {Object} formData - Die aktuellen Formulardaten
 * @param {number} currentStep - Der aktuelle Schritt (1-4)
 * @param {string} draftId - Optional: ID des bestehenden Entwurfs
 * @param {Function} onDraftIdChange - Callback wenn draftId sich ändert (z.B. neuer Draft erstellt)
 * @param {Function} onDraftSaved - Callback wenn Draft erfolgreich gespeichert wurde (für Liste-Aktualisierung)
 * @param {boolean} isSubmitting - Optional: Wenn true, wird Auto-Save pausiert
 * @returns {Object} { saveStatus, lastSavedAt, saveDraft, isSaving, savedDraftId }
 */
function useDraftAutoSave (formData, currentStep, draftId = null, onDraftIdChange = null, onDraftSaved = null, isSubmitting = false) {
  const { user } = useAuth()
  const [saveStatus, setSaveStatus] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [savedDraftId, setSavedDraftId] = useState(draftId)
  const debounceTimerRef = useRef(null)
  const lastSavedDataRef = useRef(null)
  const savingLockRef = useRef(false) // Lock für concurrent saves (useRef für sofortige Prüfung)

  // Helper function to normalize form data for comparison
  const normalizeFormData = useCallback((data) => {
    return {
      deviceName: data.deviceName || '',
      category: data.category || 'troubleshooting',
      problemTitle: data.problemTitle || '',
      errorCode: data.errorCode || null,
      problemDescription: data.problemDescription || '',
      solution: data.solution || '',
      components: data.components || '',
      difficulty: data.difficulty || 'medium',
      estimatedTime: data.estimatedTime || null,
      toolsRequired: data.toolsRequired || '',
      images: Array.isArray(data.images) ? data.images.length : 0, // Compare length only
      lastSavedStep: data.lastSavedStep || 1
    }
  }, [])

  // Helper function to check if data has changed
  const hasDataChanged = useCallback((currentData, lastSavedData) => {
    if (!lastSavedData) return true // First save
    
    const normalizedCurrent = normalizeFormData(currentData)
    const normalizedLast = normalizeFormData(lastSavedData)
    
    return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedLast)
  }, [normalizeFormData])

  // Actual save function
  const performSave = useCallback(async () => {
    // ⭐ Lock-Check - verhindert concurrent saves (useRef für sofortige Prüfung)
    if (savingLockRef.current) {
      console.log('⚠️ Save bereits in Progress - ignoriert')
      return
    }

    // Skip if currently submitting (to avoid conflicts)
    if (isSubmitting) {
      return
    }

    // Skip if no user or no meaningful data
    if (!user || !formData.deviceName) {
      return
    }

    // Minimum validation: deviceName + (problemTitle OR solution)
    if (!formData.problemTitle && !formData.solution) {
      return
    }

    // Check if data has actually changed
    const currentData = {
      ...formData,
      lastSavedStep: currentStep
    }
    
    if (!hasDataChanged(currentData, lastSavedDataRef.current)) {
      // No changes, skip save
      return
    }

    // ⭐ Lock setzen BEVOR async operation
    savingLockRef.current = true
    setIsSaving(true)
    setSaveStatus('saving')

    // Filter images: Only save images that have URLs (already uploaded)
    // File objects can't be serialized to Firestore, so we don't save them
    const imagesToSave = (formData.images || []).filter(img => {
      // Only keep images that have a URL property (already uploaded)
      // Skip file objects that haven't been uploaded yet
      return img && (img.url || (typeof img === 'string' && img.startsWith('http')))
    }).map(img => {
      // Normalize image format for saving to draft
      // Convert to format: {url, filename, size, uploadedAt}
      if (typeof img === 'string') {
        // For URL strings, try to extract referenceName from filename
        const filename = img.split('/').pop() || 'image'
        
        return {
          url: img,
          filename: filename,
          size: 0,
          uploadedAt: new Date().toISOString(),
          referenceName: getImageReferenceName(filename)
        }
      }
      return {
        url: img.url,
        filename: img.filename || img.name || 'image',
        size: img.size || 0,
        uploadedAt: img.uploadedAt || new Date().toISOString(),
        storagePath: img.storagePath || null, // Preserve storagePath for cleanup!
        referenceName: img.referenceName || null // Preserve referenceName for @mention support
      }
    })

    try {
      const result = await draftService.saveDraft(
        {
          deviceName: formData.deviceName,
          category: formData.category || 'troubleshooting',
          problemTitle: formData.problemTitle || '',
          errorCode: formData.errorCode || null,
          problemDescription: formData.problemDescription || '',
          solution: formData.solution || '',
          components: formData.components || '',
          difficulty: formData.difficulty || 'medium',
          estimatedTime: formData.estimatedTime || null,
          toolsRequired: formData.toolsRequired || '',
          images: imagesToSave,
          lastSavedStep: currentStep,
          isQuickAdd: false
        },
        draftId
      )

      if (result.success) {
        setSaveStatus('saved')
        setLastSavedAt(new Date())
        
        // Store the saved data for comparison
        lastSavedDataRef.current = {
          ...formData,
          lastSavedStep: currentStep
        }
        
        // Update saved draft ID if it changed (new draft created)
        const isNewDraft = result.id && result.id !== draftId
        if (isNewDraft) {
          setSavedDraftId(result.id)
          if (onDraftIdChange) {
            onDraftIdChange(result.id)
          }
          // Only refresh list when a NEW draft is created, not on updates
          if (onDraftSaved) {
            onDraftSaved(result.id)
          }
        }
        // For updates, we don't need to refresh the list - the draft is already there
        
        // Reset to idle after shorter time for success (1.5s), longer for errors (5s)
        setTimeout(() => {
          setSaveStatus('idle')
        }, 1500)
      } else {
        setSaveStatus('error')
      }
    } catch (error) {
      console.error('Auto-save error:', error)
      
      // Handle case where draft was deleted while being edited
      // If draft not found (404), create a new draft instead
      if (error.code === 'not-found' || (error.details && error.details.includes('nicht gefunden'))) {
        // ⭐ WICHTIG: Prüfe ob Lock noch aktiv ist (könnte von anderem Call gesetzt sein)
        if (!savingLockRef.current) {
          console.log('Save bereits von anderem Call übernommen - Recovery übersprungen')
          return
        }
        
        try {
          console.log('Draft not found, creating new draft...')
          // Clear the draftId to create a new draft
          // Filter images the same way as in the main save
          const imagesToSave = (formData.images || []).filter(img => {
            return img && (img.url || (typeof img === 'string' && img.startsWith('http')))
          }).map(img => {
            if (typeof img === 'string') {
              return {
                url: img,
                filename: img.split('/').pop() || 'image',
                size: 0,
                uploadedAt: new Date().toISOString()
              }
            }
            return {
              url: img.url,
              filename: img.filename || img.name || 'image',
              size: img.size || 0,
              uploadedAt: img.uploadedAt || new Date().toISOString(),
              storagePath: img.storagePath || null // Preserve storagePath for cleanup!
            }
          })

          const newResult = await draftService.saveDraft(
            {
              deviceName: formData.deviceName,
              category: formData.category || 'troubleshooting',
              problemTitle: formData.problemTitle || '',
              errorCode: formData.errorCode || null,
              problemDescription: formData.problemDescription || '',
              solution: formData.solution || '',
              components: formData.components || '',
              difficulty: formData.difficulty || 'medium',
              estimatedTime: formData.estimatedTime || null,
              toolsRequired: formData.toolsRequired || '',
              images: imagesToSave,
              lastSavedStep: currentStep,
              isQuickAdd: false
            },
            null // Create new draft
          )
          
          if (newResult.success && newResult.id) {
            setSaveStatus('saved')
            setLastSavedAt(new Date())
            setSavedDraftId(newResult.id)
            
            // Store the saved data for comparison
            lastSavedDataRef.current = {
              ...formData,
              lastSavedStep: currentStep
            }
            
            // Update parent with new draft ID
            if (onDraftIdChange) {
              onDraftIdChange(newResult.id)
            }
            
            // Notify parent that new draft was created (refresh list)
            if (onDraftSaved) {
              onDraftSaved(newResult.id)
            }
            
            // Reset to idle after shorter time for success
            setTimeout(() => {
              setSaveStatus('idle')
            }, 1500)
            return // Successfully recovered
          }
        } catch (recoveryError) {
          console.error('Failed to recover from deleted draft:', recoveryError)
        }
      }
      
      setSaveStatus('error')
      // Keep error visible longer (5 seconds)
      setTimeout(() => {
        setSaveStatus('idle')
      }, 5000)
    } finally {
      // ⭐ Lock immer freigeben
      savingLockRef.current = false
      setIsSaving(false)
    }
  }, [formData, currentStep, draftId, user, onDraftIdChange, onDraftSaved, hasDataChanged, isSubmitting])

  // Debounced Auto-Save Funktion (2-3 Sekunden Inaktivität)
  const debouncedSave = useCallback(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // ⭐ Prüfe Lock bevor Timer gesetzt wird
    if (savingLockRef.current) {
      console.log('Save läuft bereits - Debounce übersprungen')
      return
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      // ⭐ Nochmal prüfen vor Ausführung (Lock könnte inzwischen gesetzt worden sein)
      if (!savingLockRef.current && !isSubmitting) {
        await performSave()
      }
    }, 2500) // 2.5 Sekunden (Best Practice)
  }, [performSave, isSubmitting])

  // Manual save function (exposed to component)
  const saveDraft = useCallback(async () => {
    await performSave()
  }, [performSave])

  // Auto-save on form data changes
  useEffect(() => {
    // Skip auto-save if currently submitting
    if (isSubmitting) {
      // Clear any pending saves
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      return
    }

    // Only auto-save if we have meaningful data
    if (formData.deviceName && (formData.problemTitle || formData.solution)) {
      debouncedSave()
    }

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [formData, debouncedSave, isSubmitting])

  // Save on beforeunload (last chance save)
  // Note: Modern browsers limit beforeunload behavior, so we use visibilitychange as well
  useEffect(() => {
    const handleBeforeUnload = event => {
      // Skip if currently submitting or saving
      if (isSubmitting || savingLockRef.current) {
        return
      }
      
      // Only trigger if we have unsaved data
      if (formData.deviceName && (formData.problemTitle || formData.solution)) {
        // Filter images before saving
        const imagesToSave = (formData.images || []).filter(img => {
          return img && (img.url || (typeof img === 'string' && img.startsWith('http')))
        }).map(img => {
          if (typeof img === 'string') {
            return {
              url: img,
              filename: img.split('/').pop() || 'image',
              size: 0,
              uploadedAt: new Date().toISOString()
            }
          }
          return {
            url: img.url,
            filename: img.filename || img.name || 'image',
            size: img.size || 0,
            uploadedAt: img.uploadedAt || new Date().toISOString(),
            storagePath: img.storagePath || null // Preserve storagePath for cleanup!
          }
        })

        // Try to save synchronously (may not complete)
        draftService.saveDraft(
          {
            deviceName: formData.deviceName,
            category: formData.category || 'troubleshooting',
            problemTitle: formData.problemTitle || '',
            errorCode: formData.errorCode || null,
            problemDescription: formData.problemDescription || '',
            solution: formData.solution || '',
            components: formData.components || '',
            difficulty: formData.difficulty || 'medium',
            estimatedTime: formData.estimatedTime || null,
            toolsRequired: formData.toolsRequired || '',
            images: imagesToSave,
            lastSavedStep: currentStep,
            isQuickAdd: false
          },
          draftId
        ).catch(error => {
          console.error('Beforeunload save error:', error)
        })
      }
    }

    const handleVisibilityChange = () => {
      // Skip if currently submitting or saving
      if (isSubmitting || savingLockRef.current) {
        return
      }
      
      // Save when page becomes hidden (user switches tabs, minimizes, etc.)
      if (document.hidden && formData.deviceName && (formData.problemTitle || formData.solution)) {
        performSave()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [formData, currentStep, draftId, performSave, isSubmitting])

  // Update savedDraftId when draftId prop changes
  useEffect(() => {
    // ⭐ Nur updaten wenn nicht gerade gespeichert wird
    if (!savingLockRef.current && draftId !== savedDraftId) {
      setSavedDraftId(draftId)
      // ⭐ Auch lastSavedDataRef zurücksetzen wenn draftId sich ändert (neuer Draft geladen)
      lastSavedDataRef.current = null
    }
  }, [draftId, savedDraftId])

  // ⭐ Cleanup Lock bei Unmount
  useEffect(() => {
    return () => {
      savingLockRef.current = false
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    saveStatus,
    lastSavedAt,
    saveDraft,
    isSaving,
    savedDraftId
  }
}

export default useDraftAutoSave

